import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getPascoService } from '@src/container';
import type { PascoDTO } from '@src/application/pascos/dto';
import type { StudentUploadPascoData } from '@src/domain/pascos/value_objects/student_upload_pasco_data';

interface StudentUploadPascoVariables {
    data: StudentUploadPascoData;
    onUploadProgress?: (progress: number) => void;
}

/**
 * Hook: useStudentUploadPasco
 * Allows students to upload PASCO files with optional university selection
 * 
 * @param options - React Query mutation options
 * @returns Mutation result for student PASCO upload
 * 
 * @example
 * ```typescript
 * const { mutate: uploadPasco, isPending, error } = useStudentUploadPasco({
 *     onSuccess: (uploadedPasco) => {
 *         console.log('PASCO uploaded:', uploadedPasco);
 *     }
 * });
 * 
 * // Usage
 * uploadPasco({
 *     data: { file, university_id },
 *     onUploadProgress: (progress) => setProgress(progress)
 * });
 * ```
 */
export function useStudentUploadPasco(
    options?: Omit<
        UseMutationOptions<PascoDTO, Error, StudentUploadPascoVariables>,
        'mutationFn'
    >
) {
    const pascoService = getPascoService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async ({ data, onUploadProgress }: StudentUploadPascoVariables) => {
            return await pascoService.studentUpload(data, onUploadProgress);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Invalidate and refetch pascos list
            await queryClient.invalidateQueries({
                queryKey: ['pascos'],
                predicate: (query) => query.queryKey.length === 1 // Only invalidate list queries
            });

            // Optionally set the query data for this specific pasco
            queryClient.setQueryData(['pascos', data.pascoId], data);

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        },
    });
}
